import { useQuery } from '@zg-rentals/react-query5';
import { useDispatch } from 'react-redux';

import useRequestConfig from '../../useRequestConfig';

import PropertiesActions from '../../../actions/PropertiesActions';

import makeRequest from '../../../utils/makeRequestUtils';

import { RENTAL_MANAGER_API_V1_PREFIX } from '../../../constants/RouteConstants';
import TimeConstants from '../../../constants/TimeConstants';

import ApplicationSummary from '../../../model/ApplicationSummary';
import Syndication from '../../../model/Syndication';

import type { Dispatch } from 'react';
import type { QueryClient } from '@zg-rentals/react-query5';
import type { MakeRequestError } from '../../../utils/makeRequestUtils';
import type { ApplicationSummaryProps } from '../../../model/ApplicationSummary';
import type { CurrentPropertyProps } from '../../../model/CurrentProperty';
import type { LeasesSummaryResponse, ListingSummaryResponse } from './types';

const useGetLeasesSummary = (listingAlias: string, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/leases`,
      requestConfig,
      config: { params: { propertyId: listingAlias } },
    });

  return useQuery<LeasesSummaryResponse, MakeRequestError>({
    queryKey: ['getLeasesSummary', listingAlias],
    queryFn: async () => {
      const data = await queryFn();

      return data;
    },
    ...queryOptions,
    enabled: Boolean(listingAlias),
    staleTime: TimeConstants.SECONDS_10,
    retry: false,
  });
};

/**
 * when using this query we need to sync data to the redux store in high level components
 * child components or components that only consume data do not need to sync data
 *
 * high level components:
 *   ConnectedListing
 *
 * implement by calling syncCurrentProperty on data change
 *
 *   useEffect(() => {
 *     if (listingSummaryData) {
 *       syncCurrentProperty({ data: listingSummaryData, dispatch });
 *     }
 *   }, [listingSummaryData]);
 */
const useGetListingSummary = (listingAlias: string, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/listing`,
      requestConfig,
      config: { params: { listingAlias } },
    });

  return useQuery<ListingSummaryResponse, MakeRequestError>({
    queryKey: ['getListingSummary', listingAlias],
    queryFn: async () => {
      const data = await queryFn();

      return data;
    },
    ...queryOptions,
    enabled: Boolean(listingAlias),
    staleTime: TimeConstants.SECONDS_10,
  });
};

const syncCurrentProperty = ({
  data,
  dispatch,
}: {
  data: ListingSummaryResponse;
  dispatch: Dispatch<{ type: string; payload: Partial<CurrentPropertyProps> }>;
}) => {
  const dataToUpdate = {
    active: data.isActive,
    isFeed: data.isFeed,
    hasAnalyticsRentComps: data.hasAnalyticsRentComps,
    expirationDateTimestamp: data.dateExpirationTimestamp || '',
    // @ts-expect-error
    syndication: Syndication(data.syndication),
    dateLastActivatedTimestamp: data.dateActivatedTimestamp || '',
    contactName: data.contactName,
    canBeRelisted: data.canBeRelisted,
  };

  // @ts-expect-error
  dispatch(PropertiesActions.updateCurrentProperty(dataToUpdate));
};

/**
 * there are cases where the api response returns a 200 status, but
 * we still want to display an Alert.
 *
 * check for this within the component and display a client error message
 *
 * if we trigger a Promise.reject or throw Error the react-query state will not be correct
 * need to investigate for documentation around this
 *
 * ex: restrictions
 */
const useGetApplicationsSummary = (listingAlias: string, getActive = false, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/summary/applications`,
      requestConfig,
      config: { params: { listingAlias, getActive } },
    });

  return useQuery<ReturnType<typeof ApplicationSummary>, MakeRequestError>({
    queryKey: ['getApplicationsSummary', listingAlias, getActive],
    queryFn: async () => {
      const data = await queryFn();

      return ApplicationSummary(data);
    },
    ...queryOptions,
    enabled: Boolean(listingAlias),
    staleTime: TimeConstants.SECONDS_10,
  });
};

const setQueryApplicationsSummary = ({
  config,
  payload,
}: {
  config: { queryClient: QueryClient; listingAlias: string; getActive: boolean };
  payload: Partial<ApplicationSummaryProps>;
}) => {
  const { queryClient, listingAlias, getActive } = config;

  queryClient.setQueryData(['getApplicationsSummary', listingAlias, getActive], (oldData: ApplicationSummaryProps) => {
    // Only update if we have existing data (don't clear error states)
    if (!oldData) {
      return oldData; // Return undefined to preserve error state
    }

    return {
      ...ApplicationSummary(oldData),
      ...payload,
    };
  });
};

export {
  useGetApplicationsSummary,
  useGetLeasesSummary,
  useGetListingSummary,
  setQueryApplicationsSummary,
  syncCurrentProperty,
};
